import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ContactsData } from '@bazis/form/models/form-element.types';
import { EmailValidators } from '@bazis/form/validators/emailValidators';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import {
    PHONE_INPUT_TRANSFORM_FN,
    PHONE_MASK_SETTINGS,
    PHONE_PATTERN,
} from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-contacts',
    templateUrl: './input-contacts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputContactsComponent extends BazisControlValueAccessor implements OnInit {
    @Input()
    min = 1;

    @Input()
    max = 1;

    @Input()
    titleItemKey = '';

    @Input()
    addButtonTitleKey = '';

    @Input()
    removeButtonTitleKey = '';

    @Input()
    phoneInputTransform = PHONE_INPUT_TRANSFORM_FN;

    @Input()
    phoneMaskSettings = PHONE_MASK_SETTINGS;

    @Input()
    phonePattern = PHONE_PATTERN;

    @Input()
    anchorRoot = '';

    // вид кнопки или ссылки для добавления группы
    @Input() addedGroupIsButton: boolean = true;

    disabled = false;

    contactsForm = new FormGroup({ contacts: new FormArray([]) });

    contacts = this.contactsForm.controls.contacts as FormArray;

    valueChanges$ = this.contactsForm.valueChanges.pipe(
        debounceTime(500),
        tap((value) => {
            this.onChange(value.contacts);
        }),
    );

    required = new TemplateObservable(true);

    ngOnInit() {
        super.ngOnInit();
        this.setValidators();
        this.contactsForm.updateValueAndValidity();
    }

    extendOnInit() {
        if (this.min > this.max) {
            this.max = this.min;
        }
    }

    setValidators() {
        if (!this.ngControl.control) return;
        if (!this.ngControl.control.hasValidator(this.validate)) {
            this.ngControl.control.setValidators([this.validate.bind(this)]);
            this.ngControl.control.updateValueAndValidity();
        }
    }

    validate({ value }: FormControl) {
        if (this.contactsForm.status === 'VALID') {
            return null;
        }

        let componentError;
        this.contacts.controls.forEach((contact, index) => {
            if (contact.errors?.contactLastNameEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-last_name-' + index,
                    titleKey: 'form.title.contact.last_name',
                };
            }
            if (contact.errors?.contactFirstNameEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-first_name-' + index,
                    titleKey: 'form.title.contact.first_name',
                };
            }
            if (contact.errors?.contactPhoneEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-phone-' + index,
                    titleKey: 'form.title.contact.phone',
                };
            }
            if (contact.errors?.contactEmailEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-email-' + index,
                    titleKey: 'form.title.contact.email',
                };
            }
        });

        return {
            componentError: componentError
                ? componentError
                : this.contacts.controls.map((contact) => contact.status === 'VALID'),
        };
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.contactsForm.disable();
        } else {
            this.contactsForm.enable();
        }
    }

    public writeValue(value): void {
        if (!this.ngControl.control) return;
        this.contacts.clear({ emitEvent: false });

        this.setValidators();

        if (!value || !Array.isArray(value)) {
            this.init();
            return;
        }
        value.forEach((contact) => {
            this.contacts.push(this.generateContact({ ...contact }));
        });
    }

    init() {
        for (let i = 0; i < this.min; i++) {
            this.contacts.push(this.generateContact());
        }
    }

    generateContact(data: ContactsData = null) {
        return new FormGroup(
            {
                first_name: new FormControl(data?.first_name, [Validators.required]),
                last_name: new FormControl(data?.last_name, [Validators.required]),
                middle_name: new FormControl(data?.middle_name),
                phone: new FormControl(data?.phone, [
                    Validators.required,
                    Validators.pattern(this.phonePattern),
                ]),
                phone_additional: new FormControl(data?.phone_additional, [
                    Validators.max(999999999),
                ]),
                position: new FormControl(data?.position),
                email: new FormControl(data?.email, [
                    Validators.required,
                    EmailValidators.emailValidatorFn(),
                ]),
            },
            [this.validateContactItem],
        );
    }

    validateContactItem(item) {
        if (!item.controls.last_name.value) return { contactLastNameEmpty: true };
        if (!item.controls.first_name.value) return { contactFirstNameEmpty: true };
        if (!item.controls.phone.value) return { contactPhoneEmpty: true };
        if (!item.controls.email.value) return { contactEmailEmpty: true };

        return null;
    }

    addContactGroup() {
        this.contacts.push(this.generateContact());
    }

    removeContact(i: number) {
        this.contacts.removeAt(i);
    }
}
